import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui';
import { useCategories } from '../hooks/useCategories';
import { ArrowRight, Truck, ShoppingCart, MapPin, LayoutGrid } from 'lucide-react';

export default function Home() {
  const { categories: backendCats } = useCategories();

  const allCategories = [
    { name: 'Todos', Icon: LayoutGrid },
    ...backendCats.map(c => ({ name: c.name, Icon: c.Icon })),
  ];

  return (
    <div className="flex flex-col min-h-screen bg-white ">
      {/* Hero */}
      <section className="relative overflow-hidden px-6 lg:px-16 py-20 lg:py-32 flex flex-col justify-center items-center text-center h-[100vh]">
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-[120px] -translate-y-1/2 pointer-events-none" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-indigo-500/10 rounded-full blur-[100px] -translate-y-1/2 pointer-events-none" />

        <div className="max-w-3xl space-y-6 relative z-10">
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 bg-white/85 border border-slate-200/80 px-4 py-2.5 rounded-2xl shadow-xl shadow-blue-500/5 backdrop-blur-md">
            <div className="flex items-center gap-2">
              <div className="bg-blue-50 border border-blue-500/10 p-2 rounded-xl text-blue-500 shadow-sm flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 animate-bounce" style={{ animationDuration: '3s' }} />
              </div>
              <span className="font-heading font-semibold text-xs text-slate-700 tracking-wide text-left">
                Aye Otero Calzados · Belgrano 422, Capitán Sarmiento
              </span>
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Belgrano+422,+Capitán+Sarmiento,+Buenos+Aires"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-500 hover:bg-blue-600 text-white font-heading font-bold text-[10px] rounded-xl shadow-md shadow-blue-500/10 transition-all active:scale-95 whitespace-nowrap uppercase tracking-wider"
            >
              Ver en Google Maps
            </a>
          </div>

          <div className="flex justify-center py-4">
            <div className="relative group">
              <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-400 via-indigo-400 to-indigo-500 rounded-full blur opacity-25 group-hover:opacity-60 transition duration-500 group-hover:duration-200 pointer-events-none" />
              <div className="relative bg-white/90 backdrop-blur-md border border-slate-200/80 p-3.5 rounded-full shadow-xl transition-transform duration-500 group-hover:scale-[1.03]">
                <img
                  src="/icon-original.png"
                  alt="Aye Otero Calzados Logo"
                  className="w-32 h-32 md:w-40 md:h-40 object-contain rounded-full bg-white"
                  onError={(e) => { (e.target as HTMLImageElement).src = '/icon.png'; }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <span className="text-[10px] text-white font-extrabold uppercase tracking-[0.25em] bg-red-400 border border-blue-500/15 px-4 py-1.5 rounded-full shadow-sm">
              Nueva Colección Importada
            </span>
          </div>

          <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Descubrí nuestra colección exclusiva de calzado artesanal de cuero legítimo, buzos oversize con frisa premium y accesorios diseñados para marcar tendencia.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-2">
            <div className="relative group">
              <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-400 via-indigo-400 to-indigo-500 rounded-full blur opacity-25 group-hover:opacity-60 transition duration-500 pointer-events-none" />
              <Button
                href="/catalogo"
                variant="primary"
                size="lg"
                className="relative z-10"
                rightIcon={<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
              >
                Explorar Catálogo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Info Row */}
      <section className="px-6 lg:px-16 py-10 bg-white/75 border-y border-slate-200/80 grid grid-cols-1 md:grid-cols-3 gap-8 shadow-sm backdrop-blur-sm">
        <div className="flex items-start gap-4">
          <div className="bg-indigo-500/10 p-3 rounded-xl border border-indigo-500/20 text-indigo-700 flex-shrink-0 shadow-sm">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <h5 className="font-semibold text-slate-900 text-sm">Envíos a Todo el País</h5>
            <p className="text-slate-500 text-xs mt-1">Llegamos a cada rincón de la Argentina. Recibí tu pedido de forma rápida y segura.</p>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="bg-indigo-500/10 p-3 rounded-xl border border-indigo-500/20 text-indigo-700 flex-shrink-0 shadow-sm">
            <ShoppingCart className="w-5 h-5" />
          </div>
          <div>
            <h5 className="font-semibold text-slate-900 text-sm">Pedidos por WhatsApp</h5>
            <p className="text-slate-500 text-xs mt-1">Armá tu carrito de compras directamente en la página web y envialo a nuestro chat con un solo click.</p>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="bg-indigo-500/10 p-3 rounded-xl border border-indigo-500/20 text-indigo-700 flex-shrink-0 shadow-sm">
            <MapPin className="w-5 h-5" />
          </div>
          <div>
            <h5 className="font-semibold text-slate-900 text-sm">Local en Capitán Sarmiento</h5>
            <p className="text-slate-500 text-xs mt-1">Visitá nuestro showroom exclusivo ubicado en Belgrano 422, Capitán Sarmiento, Buenos Aires.</p>
          </div>

        </div>
      </section>

      {/* Categories */}
      <section className="px-6 lg:px-16 py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto space-y-10">
          <div className="text-center space-y-2">
            <span className="text-[10px] text-blue-600 font-extrabold uppercase tracking-[0.2em]">Categorías Principales</span>
            <h3 className="font-heading font-bold text-2xl text-slate-900">¿Qué estás buscando hoy?</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {allCategories.map(({ name, Icon }) => (
              <Link
                key={name}
                to={name === 'Todos' ? '/catalogo' : `/catalogo?categoria=${encodeURIComponent(name)}`}
                className="flex flex-col items-center justify-center bg-white border border-slate-200 hover:border-blue-500/30 p-6 rounded-2xl transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg hover:shadow-blue-500/5 group"
              >
                <div className="bg-slate-50 p-3.5 rounded-xl text-slate-700 group-hover:bg-blue-600/10 group-hover:text-blue-600 transition-colors mb-3">
                  <Icon className="w-6 h-6" />
                </div>
                <span className="font-heading font-bold text-slate-800 text-xs text-center">{name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-[#f3f0e9] border-t border-slate-200/80 py-8 px-6 lg:px-16 text-center text-xs text-slate-500 space-y-3">
        <p>© 2026 Aye Otero Calzados. Todos los derechos reservados.</p>
        <p>Belgrano 422, Capitán Sarmiento, Buenos Aires. Envíos a todo el país.</p>
      </footer>
    </div>
  );
}
